import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Package, CheckCircle, XCircle, Ban, AlarmClock, Truck, Clock,
  Download, Loader2, X, Search, MapPin, Calendar
} from 'lucide-react';
import StatCard from './StatCard';
import StatusBreakdownChart from './StatusBreakdownChart';
import { downloadDeliveryPerformanceExcel } from './downloadDeliveryPerformanceExcel';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Delivery Performance section for Phase1Dashboard.
 * Fetches postponed deliveries separately using the shared postponed route.
 *
 * @param {Object}   performanceData  - Response from /api/stats/delivery-performance
 * @param {number}   userId           - Provider admin user ID (for provider scoping)
 * @param {string}   period
 * @param {string}   customStartDate
 * @param {string}   customEndDate
 */
export function DeliveryPerformanceSection({ performanceData, userId, period, customStartDate, customEndDate }) {
  const [modal, setModal]                     = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [postponedItems, setPostponedItems]   = useState([]);
  const [postponedLoading, setPostponedLoading] = useState(false);

  // ── Fetch postponed whenever date range changes ─────────────
  useEffect(() => {
    if (!performanceData || !userId) return;

    async function fetchPostponed() {
      setPostponedLoading(true);
      try {
        const dateRange = performanceData.date_range;
        const res = await axios.get(`${API_URL}/api/delivery-points/postponed`, {
          params: {
            start_date:  dateRange.start,
            end_date:    dateRange.end,
            user_id:     userId,
          },
        });
        setPostponedItems(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching postponed deliveries:', err);
        setPostponedItems([]);
      } finally {
        setPostponedLoading(false);
      }
    }

    fetchPostponed();
  }, [performanceData, userId]);

  // ── Delivery lists from performanceData.deliveries ──────────
  const allDeliveries = performanceData?.deliveries ?? [];
  const byStatus      = (status) => allDeliveries.filter(d => d.status === status);

  function openModal(title, items, showReason = false) {
    setModal({ title, items, showReason });
  }

  async function handleDownload() {
    if (!performanceData?.deliveries?.length && !postponedItems.length) {
      alert('No hay datos de entregas para exportar en este periodo');
      return;
    }
    setDownloadingExcel(true);
    try {
      downloadDeliveryPerformanceExcel(
        performanceData,
        postponedItems,
        period,
        customStartDate,
        customEndDate
      );
    } catch (err) {
      console.error('Error downloading Excel:', err);
      alert('Error al descargar el archivo Excel');
    } finally {
      setDownloadingExcel(false);
    }
  }

  const cardBase = 'cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-transform';

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-900 font-montserrat flex items-center gap-2">
          <Package className="w-6 h-6 text-brand-primary" />
          Rendimiento de Entregas
        </h2>
        <button
          onClick={handleDownload}
          disabled={downloadingExcel}
          className="flex items-center gap-2 px-4 py-2 bg-brand-green text-white rounded-lg font-montserrat hover:bg-brand-green/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
        >
          {downloadingExcel
            ? <><Loader2 className="w-4 h-4 animate-spin" />Descargando...</>
            : <><Download className="w-4 h-4" />Descargar Excel</>
          }
        </button>
      </div>

      {/* Clickable stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        <div className={cardBase} onClick={() => openModal('Entregados', byStatus('entregado'))}>
          <StatCard
            icon={CheckCircle}
            label="Entregados"
            value={performanceData?.status_breakdown?.entregado || 0}
            subValue={`${performanceData?.status_percentages?.entregado || 0}%`}
            gradient="from-brand-green to-brand-gold"
          />
        </div>

        <div className={cardBase} onClick={() => openModal('Rechazados', byStatus('rechazado'))}>
          <StatCard
            icon={XCircle}
            label="Rechazados"
            value={performanceData?.status_breakdown?.rechazado || 0}
            subValue={`${performanceData?.status_percentages?.rechazado || 0}%`}
            gradient="from-red-500 to-red-600"
          />
        </div>

        <div className={cardBase} onClick={() => openModal('Cancelados', byStatus('cancelado'))}>
          <StatCard
            icon={Ban}
            label="Cancelados"
            value={performanceData?.status_breakdown?.cancelado || 0}
            subValue={`${performanceData?.status_percentages?.cancelado || 0}%`}
            gradient="from-slate-500 to-slate-600"
          />
        </div>

        <div className={cardBase} onClick={() => openModal('En Camino', byStatus('encamino'))}>
          <StatCard
            icon={Truck}
            label="En Camino"
            value={performanceData?.status_breakdown?.encamino || 0}
            subValue={`${performanceData?.status_percentages?.encamino || 0}%`}
            gradient="from-blue-500 to-blue-600"
          />
        </div>

        <div className={cardBase} onClick={() => openModal('Pendientes', byStatus('pending'))}>
          <StatCard
            icon={Clock}
            label="Pendientes"
            value={performanceData?.status_breakdown?.pending || 0}
            subValue={`${performanceData?.status_percentages?.pending || 0}%`}
            gradient="from-amber-500 to-amber-600"
          />
        </div>

        <div
          className={`${cardBase} ${postponedLoading ? 'pointer-events-none opacity-70' : ''}`}
          onClick={() => !postponedLoading && openModal('Postergados', postponedItems, true)}
        >
          <StatCard
            icon={postponedLoading ? Loader2 : AlarmClock}
            label="Postergados"
            value={postponedLoading ? '…' : postponedItems.length}
            subValue={postponedLoading ? 'Cargando...' : 'Ver motivos'}
            gradient="from-sky-500 to-sky-600"
          />
        </div>
      </div>

      {/* Chart */}
      <StatusBreakdownChart data={performanceData} />

      {/* Modal */}
      {modal && (
        <DeliveryModal
          title={modal.title}
          items={modal.items}
          showReason={modal.showReason}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ── Delivery list modal ───────────────────────────────────────────────────────

function DeliveryModal({ title, items, showReason, onClose }) {
  const [search, setSearch] = useState('');

  const filtered = items.filter(d => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      String(d.id).includes(q) ||
      (d.recipient_name ?? '').toLowerCase().includes(q) ||
      (d.username ?? '').toLowerCase().includes(q) ||
      (d.delivery_date ?? '').includes(q) ||
      (showReason && (d.postponed_reason ?? '').toLowerCase().includes(q))
    );
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-extrabold text-slate-900 font-montserrat italic">
            {title}
            <span className="ml-2 text-sm font-semibold text-slate-400 not-italic">
              ({items.length})
            </span>
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={
                showReason
                  ? 'Buscar por ID, cliente, tienda o motivo...'
                  : 'Buscar por ID, cliente, tienda o fecha...'
              }
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/30 focus:border-[#B1336E] font-montserrat"
            />
          </div>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Search className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm font-montserrat">Sin resultados</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((d, idx) => (
                <li key={d.id ?? idx} className="px-5 py-3 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    {/* ID badge */}
                    <span className="shrink-0 text-xs font-bold text-[#B1336E] bg-[#B1336E]/10 px-2 py-1 rounded font-montserrat min-w-[2.5rem] text-center">
                      #{d.id}
                    </span>

                    {/* Name + store */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 font-montserrat truncate">
                        {d.recipient_name || '—'}
                      </p>
                      <p className="text-xs text-slate-500 font-montserrat">{d.username || '—'}</p>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-1 shrink-0 text-xs text-slate-500 font-montserrat">
                      <Calendar className="w-3 h-3" />
                      <span>{showReason ? (d.postponed_from_date || d.delivery_date || '—') : (d.delivery_date || '—')}</span>
                    </div>
                  </div>

                  {/* Postponed reason */}
                  {showReason && d.postponed_reason && (
                    <div className="mt-1.5 ml-[calc(2.5rem+0.75rem)] flex items-start gap-1.5">
                      <AlarmClock className="w-3 h-3 text-sky-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-sky-700 font-montserrat leading-snug">
                        {d.postponed_reason}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
